<template>
  <section class="critical-supply-diagnostic p-3">
    <div v-if="loading" class="text-center py-5">
      <LoadingSpinner />
      <p class="text-muted mt-3 mb-0">{{ tEn('lang.critical_supply_diagnostic.loading') }}</p>
    </div>

    <div v-else-if="error" class="alert alert-danger mb-0">
      {{ error }}
    </div>

    <div v-else-if="data">
      <div class="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 class="mb-1 font-weight-bold">{{ tEn('lang.critical_supply_diagnostic.title') }}</h5>
          <div class="text-muted small">
            {{ tEn('lang.critical_supply_diagnostic.order_label', { id: data.target_id }) }} · {{ data.target_name }}
          </div>
        </div>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="$emit('close')">
          {{ tEn('lang.button.close') }}
        </button>
      </div>

      <div class="row mb-3">
        <div class="col-md-4 mb-2">
          <div class="diag-card diag-card--danger h-100">
            <div class="diag-card__label">{{ tEn('lang.critical_supply_diagnostic.card_avg_3d') }}</div>
            <div class="diag-card__value">
              {{ summary.avg_sent_3day }} / {{ summary.new_emails_target }}
            </div>
            <div class="diag-card__pct">{{ summary.avg_pct }}%</div>
            <span class="badge" :class="summary.going_up ? 'badge-success' : 'badge-danger'">
              {{ summary.going_up ? tEn('lang.critical_supply_diagnostic.trend_up') : tEn('lang.critical_supply_diagnostic.trend_down') }}
            </span>
          </div>
        </div>
        <div class="col-md-4 mb-2">
          <div class="diag-card diag-card--warning h-100">
            <div class="diag-card__label">{{ tEn('lang.critical_supply_diagnostic.card_tomorrow') }}</div>
            <div class="diag-card__value">
              {{ summary.tomorrow_emails }} / {{ summary.new_emails_target_tm }}
            </div>
            <div class="diag-card__pct">{{ summary.tomorrow_pct }}%</div>
            <span class="badge" :class="summary.tomorrow_going_up ? 'badge-success' : 'badge-danger'">
              {{ summary.tomorrow_going_up ? tEn('lang.critical_supply_diagnostic.trend_up') : tEn('lang.critical_supply_diagnostic.trend_down') }}
            </span>
          </div>
        </div>
        <div class="col-md-4 mb-2">
          <div class="diag-card diag-card--info h-100">
            <div class="diag-card__label">{{ tEn('lang.critical_supply_diagnostic.card_available') }}</div>
            <div class="diag-card__value">{{ summary.available_contacts_order }}</div>
            <div class="diag-card__sub">
              {{ tEn('lang.critical_supply_diagnostic.imported') }} {{ summary.total_imported }} / {{ summary.total_contacts }}
            </div>
            <div class="diag-card__sub">
              {{ tEn('lang.critical_supply_diagnostic.active_mailboxes', { count: summary.active_campaigns }) }}
            </div>
          </div>
        </div>
      </div>

      <div class="diag-panel mb-3">
        <h6 class="font-weight-bold mb-2">{{ tEn('lang.critical_supply_diagnostic.volume_vs_target') }}</h6>
        <div class="diag-compare-row">
          <span class="diag-compare-label">{{ tEn('lang.critical_supply_diagnostic.daily_target') }}</span>
          <div class="diag-bar-track">
            <div class="diag-bar diag-bar--target" :style="barStyle(summary.new_emails_target, summary.new_emails_target)" />
          </div>
          <span class="diag-compare-num">{{ summary.new_emails_target }}</span>
        </div>
        <div class="diag-compare-row">
          <span class="diag-compare-label">{{ tEn('lang.critical_supply_diagnostic.avg_3d') }}</span>
          <div class="diag-bar-track">
            <div class="diag-bar diag-bar--avg" :style="barStyle(summary.avg_sent_3day, summary.new_emails_target)" />
          </div>
          <span class="diag-compare-num">{{ summary.avg_sent_3day }}</span>
        </div>
        <div class="diag-compare-row">
          <span class="diag-compare-label">{{ tEn('lang.critical_supply_diagnostic.tomorrow') }}</span>
          <div class="diag-bar-track">
            <div class="diag-bar diag-bar--tomorrow" :style="barStyle(summary.tomorrow_emails, summary.new_emails_target)" />
          </div>
          <span class="diag-compare-num">{{ summary.tomorrow_emails }}</span>
        </div>
      </div>

      <div class="diag-panel mb-3">
        <h6 class="font-weight-bold mb-2">{{ tEn('lang.critical_supply_diagnostic.send_trend') }}</h6>
        <div class="diag-trend">
          <div
            v-for="point in data.send_trend"
            :key="point.date"
            class="diag-trend-col"
            :class="{ 'diag-trend-col--weekend': point.is_weekend }"
          >
            <div
              class="diag-trend-bar"
              :style="trendBarStyle(point.first_step)"
              :title="`${point.date}: ${point.first_step}`"
            />
            <small class="diag-trend-label">{{ trendLabel(point.date) }}</small>
          </div>
        </div>
      </div>

      <div class="diag-panel mb-3">
        <div class="d-flex flex-wrap align-items-center mb-2">
          <h6 class="font-weight-bold mb-0 mr-3">{{ tEn('lang.critical_supply_diagnostic.mailbox_distribution') }}</h6>
          <span class="badge badge-danger mr-1">
            {{ tEn('lang.critical_supply_diagnostic.bucket_empty') }}: {{ bucketCount('empty') }}
          </span>
          <span class="badge badge-warning mr-1">
            {{ tEn('lang.critical_supply_diagnostic.bucket_pipeline_only') }}: {{ bucketCount('pipeline_only') }}
          </span>
          <span class="badge badge-info mr-1">
            {{ tEn('lang.critical_supply_diagnostic.bucket_partial') }}: {{ bucketCount('partial') }}
          </span>
          <span class="badge badge-success">
            {{ tEn('lang.critical_supply_diagnostic.bucket_full') }}: {{ bucketCount('full') }}
          </span>
          <span class="badge badge-warning">
            {{ tEn('lang.critical_supply_diagnostic.bucket_warming_up') }}: {{ warmingUpCount }}
          </span>
        </div>
        <div class="diag-mailbox-grid">
          <div
            v-for="campaign in data.campaigns"
            :key="campaign.campaign_id"
            class="diag-mailbox"
            :class="mailboxClasses(campaign)"
            :title="mailboxTitle(campaign)"
          >
            <div class="diag-mailbox__email-row">
              <div class="diag-mailbox__email">{{ shortEmail(campaign.email) }}</div>
              <span
                v-if="campaign.owarm_status_id"
                class="badge diag-mailbox__owarm-badge"
                :class="owarmBadgeClass(campaign)"
              >
                {{ owarmLabel(campaign) }}
              </span>
            </div>
            <div class="diag-mailbox__stats">
              <div class="diag-mailbox__stat-row">
                <span class="diag-mailbox__stat-label">{{ tEn('lang.critical_supply_diagnostic.mailbox_contacts_activation') }}</span>
                <span class="diag-mailbox__stat-value diag-mailbox__stat-value--muted">{{ formatActivationDate(campaign) }}</span>
              </div>
              <div class="diag-mailbox__stat-row">
                <span class="diag-mailbox__stat-label">{{ tEn('lang.critical_supply_diagnostic.mailbox_available_contacts') }}</span>
                <span class="diag-mailbox__stat-value">{{ campaign.available_contacts }}</span>
              </div>
              <div class="diag-mailbox__stat-row">
                <span class="diag-mailbox__stat-label">{{ tEn('lang.critical_supply_diagnostic.mailbox_tomorrow_label') }}</span>
                <span class="diag-mailbox__stat-value" :class="mailboxTomorrowClass(campaign)">
                  {{ mailboxTomorrowText(campaign) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="diag-panel">
        <h6 class="font-weight-bold mb-2">{{ tEn('lang.critical_supply_diagnostic.detected_issues') }}</h6>
        <div
          v-for="issue in data.issues"
          :key="issue.id"
          class="diag-issue"
          :class="`diag-issue--${issue.severity}`"
        >
          <div class="diag-issue__title">{{ issueTitle(issue) }}</div>
          <div class="diag-issue__text">{{ issueDescription(issue) }}</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { targetsService } from "@/_services";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

const ISSUE_PREFIX = "lang.critical_supply_diagnostic.issues";
const DIAG_LOCALE = "en";

export default {
  name: "CriticalSupplyDiagnosticModal",
  components: { LoadingSpinner },
  props: {
    targetId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      data: null,
    };
  },
  computed: {
    summary() {
      return (this.data && this.data.summary) || {};
    },
    trendMax() {
      if (!this.data || !this.data.send_trend || !this.data.send_trend.length) {
        return 1;
      }
      return Math.max(
        ...this.data.send_trend.map((p) => Number(p.first_step) || 0),
        1
      );
    },
    warmingUpCount() {
      if (!this.data || !this.data.campaigns) {
        return 0;
      }
      return this.data.campaigns.filter((c) => Number(c.owarm_status_id) === 1).length;
    },
  },
  watch: {
    targetId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    tEn(key, params) {
      return this.$i18n.t(key, DIAG_LOCALE, params || {});
    },
    teEn(key) {
      return this.$i18n.te(key, DIAG_LOCALE);
    },
    issueKey(issue, field) {
      return `${ISSUE_PREFIX}.${issue.id}.${field}`;
    },
    issueTitle(issue) {
      const key = this.issueKey(issue, "title");
      if (this.teEn(key)) {
        return this.tEn(key);
      }
      return issue.title || issue.id;
    },
    issueDescription(issue) {
      const key = this.issueKey(issue, "description");
      const params = issue.params || {};
      if (this.teEn(key)) {
        return this.tEn(key, params);
      }
      return issue.description || "";
    },
    load() {
      if (!this.targetId) {
        return;
      }
      this.loading = true;
      this.error = null;
      targetsService
        .getCriticalSupplyDiagnostic(this.targetId)
        .then((result) => {
          this.data = result;
          this.loading = false;
        })
        .catch(() => {
          this.error = this.tEn("lang.critical_supply_diagnostic.load_error");
          this.loading = false;
        });
    },
    barStyle(value, max) {
      const pct = max > 0 ? Math.min(100, (Number(value) / Number(max)) * 100) : 0;
      return { width: `${pct}%` };
    },
    trendBarStyle(value) {
      const pct = Math.max(4, (Number(value) / this.trendMax) * 100);
      return { height: `${pct}%` };
    },
    trendLabel(date) {
      if (!date) {
        return "";
      }
      const parts = String(date).split("-");
      return parts.length === 3 ? `${parts[2]}/${parts[1]}` : date;
    },
    shortEmail(email) {
      if (!email) {
        return "—";
      }
      const local = String(email).split("@")[0];
      return local.length > 18 ? `${local.slice(0, 16)}…` : local;
    },
    mailboxTitle(campaign) {
      return this.tEn("lang.critical_supply_diagnostic.mailbox_tooltip", {
        email: campaign.email,
        available: campaign.available_contacts,
        tomorrow: campaign.new_emails_tomorrow,
        target: campaign.email_volume,
        tomorrow_line: this.mailboxTomorrowText(campaign),
        activation: this.formatActivationDate(campaign),
        owarm: this.owarmLabel(campaign),
      });
    },
    mailboxClasses(campaign) {
      return {
        [`diag-mailbox--${campaign.bucket}`]: true,
        "diag-mailbox--warmup": Number(campaign.owarm_status_id) === 1,
      };
    },
    owarmLabel(campaign) {
      const statusId = Number(campaign.owarm_status_id);
      const keyMap = {
        1: "mailbox_owarm_warming",
        2: "mailbox_owarm_active",
        3: "mailbox_owarm_resting",
      };
      const key = keyMap[statusId] || "mailbox_owarm_unknown";
      return this.tEn(`lang.critical_supply_diagnostic.${key}`);
    },
    owarmBadgeClass(campaign) {
      const statusId = Number(campaign.owarm_status_id);
      if (statusId === 1) {
        return "badge-warning";
      }
      if (statusId === 2) {
        return "badge-success";
      }
      if (statusId === 3) {
        return "badge-secondary";
      }
      return "badge-light";
    },
    formatActivationDate(campaign) {
      if (campaign.campaign_activated_at) {
        return this.formatDateLabel(campaign.campaign_activated_at);
      }
      return this.tEn("lang.critical_supply_diagnostic.mailbox_no_activation_date");
    },
    formatDateLabel(value) {
      if (!value) {
        return "—";
      }
      const raw = String(value);
      const parts = raw.split(/[ T]/)[0].split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return raw;
    },
    mailboxTomorrowText(campaign) {
      const planned = Number(campaign.new_emails_tomorrow) || 0;
      const target = Number(campaign.email_volume) || 0;
      if (target <= 0) {
        return this.tEn("lang.critical_supply_diagnostic.mailbox_tomorrow_no_quota");
      }
      return this.tEn("lang.critical_supply_diagnostic.mailbox_tomorrow_planned", {
        planned,
        target,
      });
    },
    mailboxTomorrowClass(campaign) {
      const planned = Number(campaign.new_emails_tomorrow) || 0;
      const target = Number(campaign.email_volume) || 0;
      if (planned <= 0) {
        return "diag-mailbox__stat-value--danger";
      }
      if (target > 0 && planned < target) {
        return "diag-mailbox__stat-value--warning";
      }
      return "diag-mailbox__stat-value--ok";
    },
    bucketCount(bucket) {
      if (!this.data || !this.data.campaigns) {
        return 0;
      }
      return this.data.campaigns.filter((c) => c.bucket === bucket).length;
    },
  },
};
</script>

<style scoped>
.critical-supply-diagnostic {
  width: 100%;
  min-height: 78vh;
  max-height: 82vh;
  overflow-y: auto;
  padding: 1.25rem 1.75rem !important;
  box-sizing: border-box;
}

.diag-card {
  border-radius: 8px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  background: #fff;
}

.diag-card--danger {
  border-left: 4px solid #dc3545;
}

.diag-card--warning {
  border-left: 4px solid #ffc107;
}

.diag-card--info {
  border-left: 4px solid #17a2b8;
}

.diag-card__label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
  letter-spacing: 0.03em;
}

.diag-card__value {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
}

.diag-card__pct {
  font-size: 0.95rem;
  color: #495057;
}

.diag-card__sub {
  font-size: 0.8rem;
  color: #6c757d;
}

.diag-panel {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 14px;
  background: #fafbfc;
}

.diag-compare-row {
  display: grid;
  grid-template-columns: 120px 1fr 56px;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
}

.diag-compare-label {
  font-size: 0.8rem;
  color: #495057;
}

.diag-bar-track {
  height: 14px;
  background: #e9ecef;
  border-radius: 999px;
  overflow: hidden;
}

.diag-bar {
  height: 100%;
  border-radius: 999px;
  min-width: 2px;
}

.diag-bar--target {
  background: #6c757d;
}

.diag-bar--avg {
  background: #dc3545;
}

.diag-bar--tomorrow {
  background: #fd7e14;
}

.diag-compare-num {
  font-size: 0.85rem;
  font-weight: 600;
  text-align: right;
}

.diag-trend {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  min-height: 160px;
  overflow-x: auto;
  padding-bottom: 8px;
}

.diag-trend-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 28px;
  flex: 1;
}

.diag-trend-col--weekend .diag-trend-bar {
  background: #ced4da;
}

.diag-trend-bar {
  width: 100%;
  max-width: 22px;
  background: #0d6efd;
  border-radius: 3px 3px 0 0;
  min-height: 4px;
}

.diag-trend-label {
  font-size: 0.62rem;
  color: #6c757d;
  margin-top: 4px;
  transform: rotate(-45deg);
  white-space: nowrap;
}

.diag-mailbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}

.diag-mailbox {
  border-radius: 6px;
  padding: 8px;
  border: 1px solid #dee2e6;
  background: #fff;
  font-size: 0.78rem;
}

.diag-mailbox--empty {
  border-color: #f5c2c7;
  background: #f8d7da;
}

.diag-mailbox--pipeline_only {
  border-color: #ffecb5;
  background: #fff3cd;
}

.diag-mailbox--partial {
  border-color: #b6d4fe;
  background: #cfe2ff;
}

.diag-mailbox--full {
  border-color: #badbcc;
  background: #d1e7dd;
}

.diag-mailbox--warmup {
  border-color: #ffc107;
  background: #fff8e1;
  box-shadow: inset 0 0 0 1px rgba(255, 193, 7, 0.25);
}

.diag-mailbox__email-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 4px;
}

.diag-mailbox__owarm-badge {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.diag-mailbox__email {
  font-weight: 600;
  margin-bottom: 4px;
  word-break: break-all;
}

.diag-mailbox__stats {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: #495057;
  line-height: 1.3;
  margin-top: 4px;
}

.diag-mailbox__stat-row {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.diag-mailbox__stat-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #6c757d;
  font-weight: 600;
}

.diag-mailbox__stat-value {
  font-size: 0.82rem;
  font-weight: 600;
}

.diag-mailbox__stat-value--danger {
  color: #b02a37;
}

.diag-mailbox__stat-value--warning {
  color: #997404;
}

.diag-mailbox__stat-value--ok {
  color: #146c43;
}

.diag-mailbox__stat-value--muted {
  color: #495057;
  font-weight: 500;
  font-size: 0.78rem;
}

.diag-issue {
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: #fff;
  border-left: 4px solid #6c757d;
}

.diag-issue--danger {
  border-left-color: #dc3545;
}

.diag-issue--warning {
  border-left-color: #ffc107;
}

.diag-issue--info {
  border-left-color: #0dcaf0;
}

.diag-issue__title {
  font-weight: 700;
  margin-bottom: 4px;
}

.diag-issue__text {
  font-size: 0.9rem;
  color: #495057;
  margin: 0;
}
</style>
